import React, { ReactNode, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { slugify } from 'utils/helpers';
import { Box, BoxSection } from 'components/_new/Box';
import { Button } from 'components/_new/Button';
import { usePanel } from 'contexts/panelContext';
import { useAuth } from 'contexts/authContext';
import { useScope } from 'contexts/scopeContext';
import guideIcon from 'assets/guide.svg';
import { ContentParserOptions } from 'utils/contentParser';
import dayjs from 'dayjs';
import { Icon, IconColor } from 'components/_new/Icon';
import { KnowledgeBaseArticleContentItem } from 'types/KnowledgeBase';
import { useKnowledgeBase } from './knowledgeBaseContext';
import { FavoriteIcon } from './FavoriteIcon';
import { ReadedIcon } from './ReadedIcon';
import { ToreadIcon } from './ToreadIcon';
import { ProgramArticlesSkeleton } from './skeletons/ProgramArticlesSkeleton';
import { ArticlePresenterDialog } from './ArticlePresenterDialog';
import { ColoredIcon } from './ColoredIcon';
import { getKnowledgeBaseArticleIcon, getKnowledgeBaseArticleIconSvg } from './utils/utils';

interface ProgramArticlesProps {
	renderWrapper?: (hasArticles: boolean, children: ReactNode) => ReactNode;
	contentParserOptions?: ContentParserOptions;
}

export const ProgramArticles: React.FC<ProgramArticlesProps> = ({ renderWrapper, contentParserOptions }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { currentUser } = useAuth();
	const { currentApplicationData } = usePanel();
	const { currentProgram } = useScope();
	const { knowledgeBaseLinkPrefix, context, queries } = useKnowledgeBase();
	const [modalArticleId, setModalArticleId] = useState<number | null>(null);

	const { data, refetch, isLoading, isSuccess } = useQuery(
		[
			'KNOWLEDGE_BASE_PROGRAM_ARTICLES',
			{
				applicationId: Number(currentApplicationData?.id),
				languageCode: currentUser?.language,
				context,
				isProfessionalViewEnabled: currentUser?.isProfessionalViewEnabled,
			},
		],
		() => queries.getProgramOrganizersArticles(),
		{
			enabled: true,
		}
	);

	const openArticle = (article: KnowledgeBaseArticleContentItem) => {
		const { articleId, slug } = article;
		const pathname = `${knowledgeBaseLinkPrefix}/article/${articleId}${slug ? `-${slugify(slug)}` : ''}`;
		switch (context) {
			case 'program-admin':
				navigate({
					pathname,
				});
				break;
			case 'user-panel':
			default:
				setModalArticleId(articleId);
				break;
		}
	};

	if (isLoading) {
		return <ProgramArticlesSkeleton />;
	}

	const hasArticles = Boolean(isSuccess && data && data.length > 0);

	const content = (
		<>
			{context === 'user-panel' && (
				<ArticlePresenterDialog
					articleId={modalArticleId}
					visible={modalArticleId !== null}
					onHide={() => setModalArticleId(null)}
					onFlagChange={() => refetch()}
					knowledgeBaseLinkPrefix={knowledgeBaseLinkPrefix}
					contentParserOptions={contentParserOptions}
				/>
			)}

			<div className="flex flex-row align-items-center justify-content-between gap-3 mb-2 mr-2">
				<h3 className="text-base">{t('knowledgeBase.homepage.addedByOrganizers')}</h3>
				{currentProgram?.logotype && (
					<img
						src={currentProgram.logotype}
						alt={currentProgram.displayName}
						className="hidden sm:block"
						style={{ height: '35px', objectFit: 'contain' }}
					/>
				)}
			</div>

			<div className="flex flex-row gap-2 justify-content-start align-content-stretch overflow-x-auto">
				{data?.map((item) => {
					const { id, articleId, title, slug, info } = item;
					const pathname = `${knowledgeBaseLinkPrefix}/article/${articleId}${
						slug ? `-${slugify(slug)}` : ''
					}`;
					const isNew = dayjs().diff(info.createdAt, 'days') <= 30;
					return (
						<Link
							to={{ pathname }}
							key={id}
							className="flex article-tile"
							onClick={(event) => {
								event.preventDefault();
								openArticle(item);
							}}
							tabIndex={-1}
						>
							<Box
								hoverable
								className="w-full text-primary"
								variant="white-bordered"
								img={() => {
									const Icon = getKnowledgeBaseArticleIconSvg(item.category, item.contentType);
									return (
										<>
											<Icon width="50" height="50" />
											{isNew && (
												<span
													className="absolute overflow-hidden bg-purple-blue text-white px-2 py-1"
													style={{
														top: 0,
														right: 1,
														borderBottomLeftRadius: '0.5rem',
													}}
												>
													{t('misc.new')}
												</span>
											)}
										</>
									);
								}}
							>
								<BoxSection horizontal contentClassName="justify-content-start">
									<div className="inline-flex gap-2 align-items-center">
										{context === 'user-panel' && (
											<>
												<FavoriteIcon checked={info.flag.favorite} />
												<ToreadIcon checked={info.flag.toRead} />
												<ReadedIcon checked={info.flag.readed} />{' '}
											</>
										)}
										{['program-admin', 'coordinator'].includes(context) && (
											<>
												{info?.access?.mentor && (
													<span title={t('knowledgeBase.programPanel.article.mentorAccess')}>
														<Icon name="user-tie" className="text-purple-500 text-lg" />
													</span>
												)}
												{info?.access?.mentee && (
													<span title={t('knowledgeBase.programPanel.article.menteeAccess')}>
														<Icon name="user-graduate" className="text-blue-500 text-lg" />
													</span>
												)}
											</>
										)}
									</div>
								</BoxSection>
								<BoxSection className="flex-1">
									<span className="strong">{title}</span>
								</BoxSection>
								<BoxSection>
									<Button
										label={t('actions.open')}
										variant="gray-primary"
										block
										onClick={(event) => {
											event.stopPropagation();
											event.preventDefault();
											openArticle(item);
										}}
									/>
								</BoxSection>
							</Box>
						</Link>
					);
				})}
			</div>
		</>
	);

	if (renderWrapper) {
		return <>{renderWrapper(hasArticles, content)}</>;
	}

	if (!hasArticles) {
		return null;
	}

	return content;
};
